// shadcn's current Alert, brought forward from the LEGACY new-york copy this
// file used to hold (absolute icon + `[&>svg~*]:pl-7` indentation, no action
// slot). Two adaptations were needed, both deliberate:
//
// 1. Tailwind. Upstream targets v4; this repo is on 3.4.19, so the v4-only
//    forms are ported (each replacement verified to emit CSS against the repo's
//    own tailwind build):
//      `*:[svg]:x`            → `[&>svg]:x`              (`*:` + arbitrary selector is v4)
//      `has-data-[slot=x]:y`  → `has-[[data-slot=x]]:y`
//      `pr-18`                → `pr-16`                  (18 is not in the v3 spacing scale)
//    Upstream's `[&>svg:not([class*='size-'])]:size-4` is dropped outright: it
//    outranks a plain `h-5 w-5` on specificity, and 127 of this app's 129 Alert
//    icons size themselves that way — it would silently shrink all of them.
//
// 2. Palette. The structure is upstream's; the COLORS and box padding are this
//    app's existing ones. Upstream's current default/destructive (`bg-card`,
//    destructive with no red border, muted description text) would restyle ~100
//    live alerts, which is a separate decision from modernizing the markup.
//
// The `warning` / `success` tones are additive — amber-600 / green-600 are
// already the warning/success ink across this app (no --warning token exists).

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@trashlab/viteapp/src/lib/utils';

const alertVariants = cva(
  cn(
    'group/alert relative grid w-full gap-1 rounded-lg border px-4 py-3 text-left text-sm',
    // A direct <svg> child takes its own column and spans title + description;
    // without one the grid collapses to a single column.
    'has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-3 [&>svg]:row-span-2 [&>svg]:translate-y-0.5',
    // Keep the text clear of a corner action.
    'has-[[data-slot=alert-action]]:pr-16',
  ),
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground [&>svg]:text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
        // Tone in the border and the icon only — the text stays neutral, so
        // these read as a notice rather than as an error.
        warning: 'border-amber-600/35 text-foreground [&>svg]:text-amber-600',
        success: 'border-green-600/40 text-foreground [&>svg]:text-green-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium leading-none tracking-tight group-has-[>svg]/alert:col-start-2',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        // No colour of its own: a destructive Alert tints its body text through
        // the root, exactly as the legacy component did.
        'text-sm group-has-[>svg]/alert:col-start-2 [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

/** Corner action slot. Absolutely positioned, so it never disturbs the grid —
 *  override `className` for anything else (see quotes-v2's NoticeCard). */
function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-action"
      className={cn('absolute right-3 top-3', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction, alertVariants };
